import { Bell, Info, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';

import useViewport from '@/hooks/use-viewport';

import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

type Announcement = {
  id: string;
  legacyDismissedVersion?: string;
  renderContent: () => JSX.Element;
  title: string;
};

type ToastId = number | string;

const LEGACY_STORAGE_KEY = 'announcement-toast-dismissed';
const STORAGE_KEY_PREFIX = 'announcement-toast-dismissed';
const TOAST_ID_PREFIX = 'announcement-toast';

const ANNOUNCEMENTS: Announcement[] = [
  {
    id: 'platform-update-cctp-v1-deprecation-2026-11-03',
    title: 'Platform Update',
    renderContent: () => (
      <>
        <p className="text-vestgrey-500">
          <span className="block font-semibold text-foreground">
            CCTP V1 Deprecation
          </span>
          Circle is retiring CCTP V1, which Vest Classic uses for Solana
          deposits and withdrawals.
        </p>
        <div className="text-vestgrey-500">
          <p>
            <span className="font-semibold text-foreground">
              Since October 31:
            </span>{' '}
            Circle is gradually lowering CCTP V1 transfer limits.
          </p>
          <p>
            <span className="font-semibold text-foreground">December 1:</span>{' '}
            CCTP V1 is paused and Solana deposits and withdrawals stop.
          </p>
        </div>
        <p className="text-vestgrey-500">
          As the limits shrink, larger Solana transfers may fail. If you plan to
          withdraw to Solana, do it before December 1.
        </p>
        <a
          href="https://www.circle.com/blog/migrate-to-cctp-v2-ahead-of-cctp-v1-legacy-deprecation"
          target="_blank"
          rel="noopener noreferrer"
          className="font-semibold text-foreground underline hover:no-underline"
        >
          Read Circle&apos;s announcement →
        </a>
      </>
    ),
  },
];

function getDismissedStorageKey(id: string) {
  return `${STORAGE_KEY_PREFIX}:${id}`;
}

function getToastId(id: string) {
  return `${TOAST_ID_PREFIX}:${id}`;
}

function getStorageItem(key: string) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function setStorageItem(key: string, value: string) {
  try {
    localStorage.setItem(key, value);
  } catch {
    return;
  }
}

function isAnnouncementDismissed(announcement: Announcement) {
  if (getStorageItem(getDismissedStorageKey(announcement.id)) === 'true') {
    return true;
  }

  return (
    !!announcement.legacyDismissedVersion &&
    getStorageItem(LEGACY_STORAGE_KEY) === announcement.legacyDismissedVersion
  );
}

function getDismissedAnnouncementIds() {
  return new Set(
    ANNOUNCEMENTS.filter(isAnnouncementDismissed).map(({ id }) => id),
  );
}

function AnnouncementToastContent({
  announcement,
}: {
  announcement: Announcement;
}) {
  return (
    <div className="flex flex-col gap-1">
      <div className="mb-1 flex items-center gap-2 text-sm uppercase tracking-wide">
        <Info size={12} className="inline-block" />
        {announcement.title}
      </div>
      {announcement.renderContent()}
    </div>
  );
}

export default function AnnouncementToast() {
  const [dismissedAnnouncementIds, setDismissedAnnouncementIds] = useState<
    Set<string>
  >(getDismissedAnnouncementIds);
  const toastIdByAnnouncementIdRef = useRef<Record<string, ToastId>>({});

  const viewport = useViewport();

  const unreadAnnouncements = useMemo(
    () => ANNOUNCEMENTS.filter(({ id }) => !dismissedAnnouncementIds.has(id)),
    [dismissedAnnouncementIds],
  );

  const dismissVisibleToasts = useCallback(() => {
    Object.values(toastIdByAnnouncementIdRef.current).forEach((toastId) => {
      toast.dismiss(toastId);
    });

    toastIdByAnnouncementIdRef.current = {};
  }, []);

  const dismissAnnouncement = useCallback((announcementId: string) => {
    setStorageItem(getDismissedStorageKey(announcementId), 'true');
    setDismissedAnnouncementIds((current) => {
      if (current.has(announcementId)) return current;

      const next = new Set(current);
      next.add(announcementId);
      return next;
    });

    const toastId = toastIdByAnnouncementIdRef.current[announcementId];
    if (toastId) {
      toast.dismiss(toastId);
      delete toastIdByAnnouncementIdRef.current[announcementId];
    }
  }, []);

  useEffect(() => {
    if (viewport === 'sm') {
      dismissVisibleToasts();
      return;
    }

    const unreadAnnouncementIds = new Set(
      unreadAnnouncements.map(({ id }) => id),
    );

    Object.entries(toastIdByAnnouncementIdRef.current).forEach(
      ([announcementId, toastId]) => {
        if (unreadAnnouncementIds.has(announcementId)) return;

        toast.dismiss(toastId);
        delete toastIdByAnnouncementIdRef.current[announcementId];
      },
    );

    [...unreadAnnouncements].reverse().forEach((announcement) => {
      const toastId = getToastId(announcement.id);
      toastIdByAnnouncementIdRef.current[announcement.id] = toastId;

      toast.info(<AnnouncementToastContent announcement={announcement} />, {
        id: toastId,
        position: 'bottom-right',
        duration: Infinity,
        onDismiss: () => {
          delete toastIdByAnnouncementIdRef.current[announcement.id];
          dismissAnnouncement(announcement.id);
        },
      });
    });
  }, [
    dismissAnnouncement,
    dismissVisibleToasts,
    unreadAnnouncements,
    viewport,
  ]);

  useEffect(() => dismissVisibleToasts, [dismissVisibleToasts]);

  if (viewport !== 'sm') return null;

  return (
    <Popover>
      <PopoverTrigger
        className="relative flex h-9 w-9 items-center justify-center rounded-md text-vestgrey-100 transition-colors hover:bg-muted hover:text-foreground"
        aria-label="Announcements"
      >
        <Bell size={14} />
        {unreadAnnouncements.length > 0 && (
          <div className="absolute bottom-2 right-2 h-2 w-2 rounded-full bg-red" />
        )}
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="max-h-[min(28rem,calc(100vh-6rem))] w-[calc(100vw-2rem)] max-w-sm overflow-y-auto p-4 shadow-none"
      >
        {unreadAnnouncements.length > 0 ? (
          <div className="flex flex-col divide-y divide-border">
            {unreadAnnouncements.map((announcement) => (
              <div key={announcement.id} className="py-3 first:pt-0 last:pb-0">
                <div className="flex items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <AnnouncementToastContent announcement={announcement} />
                  </div>
                  <button
                    type="button"
                    aria-label="Dismiss announcement"
                    className="-mr-1 flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-vestgrey-100 transition-colors hover:bg-background hover:text-foreground"
                    onClick={() => dismissAnnouncement(announcement.id)}
                  >
                    <X size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-vestgrey-500">No new announcements.</p>
        )}
      </PopoverContent>
    </Popover>
  );
}
